import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { renderAgreementHtml } from '@/lib/agreements/markdown';
import {
    applyMergeFields,
    SIGNATURE_NAMESPACE,
    signaturePresentationValues,
    type SignatureParty,
} from '@/lib/agreements/merge';

interface AgreementMarkdownProps {
    /** `agreement_render`'s `rendered_md` — the canonical text, hashed as it stands. */
    renderedMd: string;
    /** The live merge map. Admin-only, and the source of the `signature.*` values. */
    values: Record<string, unknown> | null;
    /**
     * Who signs. Only the party name is read, so a document's signature rows and a
     * template's `parties` roster both satisfy it — which is what lets the template
     * preview reuse this component before any document exists.
     */
    parties: readonly SignatureParty[];
    /**
     * The template body this document was generated from. Only needed to tint EVERY merge
     * field: `rendered_md` arrives with its values already substituted, so the marks can
     * only be produced by re-merging the body locally — exactly what the edge function's
     * highlighted preview does. Never hashed, never stored.
     */
    templateBody?: string | null;
    highlight?: boolean;
    className?: string;
}

/**
 * Renders one agreement as sanitised HTML.
 *
 * TWO PASSES ARE POSSIBLE AND ONLY ONE IS EVER RUN, because `applyMergeFields` strips its
 * own marks on entry — feeding it its own output erases the highlight it just produced:
 *
 *   highlight off  →  resolve `{{signature.*}}` over the canonical text and nothing else,
 *                     so every other character on screen is a character SQL hashed.
 *   highlight on   →  re-merge the template body from one combined map, which marks every
 *                     field. The text is then a review aid and is never what gets signed.
 */
export default function AgreementMarkdown({
    renderedMd,
    values,
    parties,
    templateBody,
    highlight = false,
    className,
}: AgreementMarkdownProps) {
    const html = useMemo(() => {
        const signatureValues = signaturePresentationValues(parties, values);

        const merged = highlight && templateBody
            ? applyMergeFields(templateBody, { ...(values ?? {}), ...signatureValues }).text
            : applyMergeFields(renderedMd ?? '', signatureValues, {
                namespace: SIGNATURE_NAMESPACE,
            }).text;

        return renderAgreementHtml(merged);
    }, [renderedMd, values, parties, templateBody, highlight]);

    return (
        <div
            className={cn('agreement-doc', highlight && 'agreement-doc--highlight', className)}
            // The string is the output of `renderAgreementHtml`: markdown-it with html:false,
            // then DOMPurify against a tag allow-list that carries no anchors, no images and
            // no event attributes.
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
